/**
 * React Router v5 → v6 compatibility utilities.
 *
 * - withRouter: HOC that injects location / navigate / params into class components.
 *   Location spreads location.state back onto location for backward compat with v5
 *   custom-state-on-location patterns (e.g. location.mergeid).
 *
 * - RouteElement: Wrapper for class components rendered via <Route element>.
 *   Provides the same compat location props that withRouter gives.
 *
 * These shims can be removed once all consumers are converted to functional components.
 */
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

/**
 * Backward-compatible withRouter HOC for react-router v6.
 * Injects: location (with state spread), navigate, params.
 */
export function withRouter(Component) {
  function WithRouterWrapper(props) {
    const rawLocation = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    // Spread location.state onto location for v5 compat (e.g. location.mergeid)
    const location = rawLocation.state
      ? { ...rawLocation, ...rawLocation.state }
      : rawLocation;
    return (
      <Component
        {...props}
        location={location}
        navigate={navigate}
        params={params}
      />
    );
  }
  const name = Component.displayName || Component.name || 'Component';
  WithRouterWrapper.displayName = `withRouter(${name})`;
  return WithRouterWrapper;
}

/**
 * Wrapper component for class components rendered via <Route element>.
 * Provides location (with v5 compat state spread), navigate, and params as props.
 *
 * Usage:
 *   <Route path="/foo" element={<RouteElement Component={MyClassComp} someProp={val} />} />
 */
export function RouteElement({ Component, ...rest }) {
  const rawLocation = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const location = rawLocation.state
    ? { ...rawLocation, ...rawLocation.state }
    : rawLocation;
  return (
    <Component
      {...rest}
      location={location}
      navigate={navigate}
      params={params}
    />
  );
}

/**
 * Extract Link props from a v5-style location object.
 * Separates { pathname, search, hash } from custom state properties.
 *
 * v5: <Link to={{ pathname: '/foo', mergeid: 123 }}>
 * v6: <Link to={{ pathname: '/foo' }} state={{ mergeid: 123 }}>
 */
export function extractLinkProps(to) {
  if (typeof to === 'string') return { to };
  if (!to) return { to: '/' };
  const { pathname, search, hash, state: existingState, key, ...customState } = to;
  const target = {};
  if (pathname !== undefined) target.pathname = pathname;
  if (search !== undefined) target.search = search;
  if (hash !== undefined) target.hash = hash;
  const mergedState = { ...existingState, ...customState };
  const hasState = Object.keys(mergedState).length > 0;
  return { to: target, state: hasState ? mergedState : undefined };
}
